import {
  component$,
  useContext,
  useSignal,
  useVisibleTask$,
} from "@builder.io/qwik";
import type { ConfigDataContextType } from "../types/ConfigDataContextType";
import { getConfigData } from "../context/ConfigtDataContext";
import type { DataContextType } from "../types/DataContextType";
import { getData } from "../context/DataContext";
import type { ColorsType } from "../types/ColorsType";
import { getColors } from "../context/ColorsContext";
import * as d3 from "d3";
import { createCandle } from "../utils/helperFunctions";

// this is used to fix a weird d3 issue where the last candle is not shown on the canvas
const MAGIC_NUMBER_ONE_CANDLE = 1;
interface CandlesCanvasProps {
  id: string;
  minMaxShownPrice: any;
  shownRange: any;
}

interface ShownDataItem {
  date: Date | number;
  technicals?: {
    ema: Array<{
      value: number;
      color?: string;
    }>;
  };
}

interface MinMaxShownPrice {
  min: number;
  max: number;
}

interface ShownRange {
  start: Date | number;
  end: Date | number;
}

export default component$(({ id, candlesCanvasId }: CandlesCanvasProps) => {
  const yAxisId = `${id}-yAxis`;
  const xAxisId = `${id}-xAxis`;
  const config: ConfigDataContextType = useContext(getConfigData);
  const data: DataContextType = useContext(getData);
  const colors: ColorsType = useContext(getColors);
  const scale = 5;
  const canvas = useSignal<HTMLCanvasElement | null>(null);

  useVisibleTask$(({ track }) => {
    track(() => [
      config.canvasWidth,
      config.mainCanvasHeight,
      data.series,
      data.shownData,
      data.shownRange,
      data.minMaxShownPrice,
      data.candleWidth,
    ]);
    if (!data.candleWidth) return;

    const xScaleFunction = d3
      .scaleTime()
      .domain([
        data.shownRange.start,
        data.shownRange.end + MAGIC_NUMBER_ONE_CANDLE,
      ])
      .range([0, config.canvasWidth ?? 0]);

    const delta = data.minMaxShownPrice.max - data.minMaxShownPrice.min;
    const yScaleFunction = d3
      .scaleLinear()
      .domain([
        data.minMaxShownPrice.max + config.emptySpaceFromTopPercent * delta,
        data.minMaxShownPrice.min - config.emptySpaceFromBottomPercent * delta,
      ])
      .range([0, config.mainCanvasHeight ?? 0]);

    if (canvas.value) {
      const ctx = canvas.value.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(
        0,
        0,
        config.canvasWidth as number,
        config.mainCanvasHeight as number,
      );

      if (data.shownData.length) {
        for (let i = 0; i < data.shownData.length; i++) {
          for (let j = 0; j < data.shownData[i].prices.length; j++) {
            const height =
              yScaleFunction(0) - yScaleFunction(data.shownData[i].prices[j]);

            const x = xScaleFunction(data.shownData[i].date);
            const y = yScaleFunction(data.shownData[i].prices[j]);
            const candle = createCandle({
              ctx,
              width: data.candleWidth / data.shownData[i].prices.length,
              height: height,
              x: x + (data.candleWidth / data.shownData[i].prices.length) * j,
              y,
              color: data.series[j].color,
              scale,
            });
            // candle.rect(x, y, data.candleWidth, height);
            // ctx.strokeStyle = data.series[j].color;
            // ctx.stroke(candle);
          }
        }
      }
    }
  });

  // y-axis labels
  useVisibleTask$(({ track }) => {
    track(() => [
      data.minMaxShownPrice,
      config.emptySpaceFromTopPercent,
      config.emptySpaceFromBottomPercent,
      config.canvasWidth,
    ]);

    const delta = data.minMaxShownPrice.max - data.minMaxShownPrice.min;
    const yScaleFunction = d3
      .scaleLinear()
      .domain([
        data.minMaxShownPrice.max + config.emptySpaceFromTopPercent * delta,
        data.minMaxShownPrice.min - config.emptySpaceFromBottomPercent * delta,
      ])
      .range([0, config.mainCanvasHeight ?? 0]);

    d3.select(`#${yAxisId}`).html("");
    const yAxis = d3
      .axisRight(yScaleFunction)
      .ticks(6)
      .tickSize(config.canvasWidth ?? 0)
      .tickFormat((val: any) => {
        if (Math.abs(val) >= 1e9)
          return (val / 1e9).toFixed(2).replace(/\.0$/, "") + " B";
        if (Math.abs(val) >= 1e6)
          return (val / 1e6).toFixed(2).replace(/\.0$/, "") + " M";
        if (Math.abs(val) >= 1e3)
          return (val / 1e3).toFixed(2).replace(/\.0$/, "") + " K";
        return val;
      });

    d3.select(`#${yAxisId}`).append("g").call(yAxis);
    d3.select(`#${yAxisId} .domain`).remove();
    d3.selectAll(`#${yAxisId} g text`).attr("transform", "translate(5,0)");
    d3.selectAll(`#${yAxisId} .tick line`)
      .style("stroke-dasharray", "2")
      .style("stroke", colors.grid);
    d3.selectAll(`#${yAxisId} .tick text`).style("fill", colors.tick);
  });

  // x-axis labels
  useVisibleTask$(({ track }) => {
    track(() => [
      data.shownRange,
      config.canvasWidth,
      config.dateCanvasHeight,
      config.canvasVolumeHeightPercent,
      data.idxToDateMapping,
    ]);

    const xScaleFunction = d3
      .scaleTime()
      .domain([
        data.shownRange.start,
        data.shownRange.end + MAGIC_NUMBER_ONE_CANDLE,
      ])
      .range([0, config.canvasWidth ?? 0]);

    d3.select(`#${xAxisId}`).html("");
    const xAxis = d3
      .axisBottom(xScaleFunction)
      .ticks(20) // some magic number here
      .tickSize(0)
      .tickFormat((key: any) => {
        const idx = data.initData.findIndex((d) => d.date === key.getTime());
        return (data.idxToDateMapping && data.idxToDateMapping[idx]) ?? "";
      });
    d3.select(`#${xAxisId}`)
      .attr("transform", `translate(0, ${config.mainCanvasHeight})`)
      .append("g")
      .call(xAxis);
    d3.select(`#${xAxisId} .domain`).remove();
    if (data.candleWidth) {
      d3.selectAll(`#${xAxisId} g text`).attr(
        "transform",
        `rotate(-30) translate(${data.candleWidth / 2 - 10}, 20)`,
      );
    }
    // d3.selectAll(`#${xAxisId} .tick line`)
    //   .style("stroke-dasharray", "2")
    //   .style("stroke", colors.grid);
    // d3.selectAll(`#${xAxisId} .tick text`).style("fill", colors.tick);
  });

  return (
    <canvas
      width={(config.canvasWidth as number) * scale}
      height={(config.mainCanvasHeight as number) * scale}
      ref={(el) => (canvas.value = el)}
      id={candlesCanvasId}
      style={{
        width: config.canvasWidth + "px",
        height: config.mainCanvasHeight + "px",
        cursor: config.pan ? "all-scroll" : "",
      }}
    ></canvas>
  );
});
